import type { GameRenderContext, GameRenderer } from "../game";
import type { HexState } from "./types";

export class HexRenderer implements GameRenderer<HexState> {
  #container: HTMLElement | null = null;
  #context: GameRenderContext<HexState> | null = null;

  mount(container: HTMLElement): void {
    this.#container = container;
    container.classList.add("hex-board");
    container.setAttribute("aria-label", "Hex board");
    container.addEventListener("click", this.#click);
  }

  render(context: GameRenderContext<HexState>): void {
    if (!this.#container) return;
    this.#context = context;
    const { board, size } = context.state;
    this.#container.style.setProperty("--hex-size", String(size));
    this.#container.style.setProperty(
      "--hex-row-overlap",
      `${-(28.8675 / size)}%`,
    );

    const files = Array.from(
      { length: size },
      (_, column) => `<span>${String.fromCharCode(65 + column)}</span>`,
    ).join("");
    const rows = Array.from({ length: size }, (_, row) => {
      const cells = Array.from({ length: size }, (_unused, column) => {
        const action = row * size + column;
        const stone = board[action];
        const coordinate = `${String.fromCharCode(65 + column)}${row + 1}`;
        const legal = stone === -1 && context.interactive;
        const classes = [
          "hex-cell",
          stone === 0 ? "hex-red" : stone === 1 ? "hex-blue" : "",
          action === context.lastModelAction?.action ? "ai-move" : "",
        ].filter(Boolean).join(" ");
        const occupant = stone === 0 ? ", Red stone" : stone === 1 ? ", Blue stone" : "";
        return `<button class="${classes}" role="gridcell" data-action="${action}"
          aria-label="${coordinate}${occupant}" title="${coordinate}" ${legal ? "" : "disabled"}></button>`;
      }).join("");
      const shift = row * (50 / size);
      return `<div class="hex-row" role="row" style="--hex-shift:${shift}%">
        <span class="hex-rank hex-rank-left" aria-hidden="true">${row + 1}</span>
        ${cells}
        <span class="hex-rank hex-rank-right" aria-hidden="true">${row + 1}</span>
      </div>`;
    }).join("");

    this.#container.innerHTML = `
      <div class="hex-files" aria-hidden="true">${files}</div>
      <div class="hex-rows">${rows}</div>`;
  }

  dispose(): void {
    this.#container?.removeEventListener("click", this.#click);
    this.#container?.classList.remove("hex-board");
    if (this.#container) this.#container.innerHTML = "";
    this.#container = null;
  }

  #click = (event: Event): void => {
    if (!this.#context?.interactive) return;
    const target = (event.target as HTMLElement).closest<HTMLElement>("[data-action]");
    if (target) this.#context.onAction(Number(target.dataset.action));
  };
}
